import { memo, SetStateAction, useEffect, useRef, useState } from 'react';
import { Divider } from 'primereact/divider';
import { useDebounce } from 'primereact/hooks';
import { useWindowSize } from 'react-use';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { AlertVariant, MOBILE_BREAKPOINT } from '../../../../../constants/constants';
import { _DatatablePageEvent, _Pagination, _ToastMessageProps } from '../../../../../constants/staticTypes';
import MlxSearch from '../../../../../components/common/MlxSearch/MlxSearch';
import logger from '../../../../../utils/logger';
import { COPY_MENU_ITEMS_FROM, COPY_MENU_ITEMS_TO, MENU_LIST, MENU_MANAGEMENT, SELECT_VENUE } from '../../../../../constants/strings';
import { useTranslation } from '../../../../../hooks/useTranslation';
import { VENUE_USERS_COPY } from '../../../../../constants/queryKeys';
import MlxBlockUI from '../../../../../components/common/MlxBlockUI/MlxBlockUI';
import MlxButton from '../../../../../components/common/MlxButton/MlxButton';
import HttpServiceHelper from '../../../../../helpers/httpServiceHelper';
import { defaultPaginationEvent } from '../../../../../helpers/commonHelper';
import MlxDialog from '../../../../../components/common/MlxDialog/MlxDialog';
import {
    _UsersBulkCopyPayloadType,
    _VenueUser,
    buildUserCopyPayload,
    bulkCopyUsers,
    getVenueUsersList,
} from '../../../../../helpers/venueUsersHelper';
import VenueUsersList from './VenueUsersList';

const languageTexts = {
    MENU_MANAGEMENT: MENU_MANAGEMENT,
    MENU_LIST: MENU_LIST,
    SELECT_VENUE: SELECT_VENUE,
    COPY_MENU_ITEMS_TO: COPY_MENU_ITEMS_TO,
    COPY_MENU_ITEMS_FROM: COPY_MENU_ITEMS_FROM,
};

type Props = {
    visible: boolean;
    setVisible: React.Dispatch<SetStateAction<boolean>>;
    selectedVenueOption?: string; // the current venue
    selectedCopyVenueOption?: string; // the venue from which the menu is to be copied
    onCopyFromSuccess: () => void;
    venueOptions?: any[];
    setToastMessage: React.Dispatch<SetStateAction<_ToastMessageProps>>;
    copyType?: 'copy-to' | 'copy-from' | '';
};

const searchColumns = ['email'];

function CopyVenueUsersModal({
    visible,
    setVisible,
    selectedVenueOption,
    selectedCopyVenueOption,
    onCopyFromSuccess,
    venueOptions,
    setToastMessage,
    copyType,
}: Readonly<Props>) {
    const { width: screenWidth } = useWindowSize();
    const [searchValue, debouncedSearchValue, setSearchValue] = useDebounce('', 0);
    const queryClient = useQueryClient();
    const translatedText = useTranslation(languageTexts, 'none');
    const userBulkCopyMutation = useMutation(HttpServiceHelper);

    const [searchBarVisible, setSearchBarVisible] = useState(screenWidth > MOBILE_BREAKPOINT);
    const [isCopyInProgress, setIsCopyInProgress] = useState(false);
    const [dataTableEvent, setDataTableEvent] = useState<_DatatablePageEvent>(defaultPaginationEvent);
    const [venueUsersListData, setVenueUsersListData] = useState<any>(null);
    const [selectedProductsForBulkCopy, setSelectedProductsForBulkCopy] = useState<_VenueUser[]>([]);

    const venueUsersListQuery = useQuery({
        queryKey: VENUE_USERS_COPY,
        queryFn: () =>
            getVenueUsersList(
                queryClient,
                (dataTableEvent.page ?? 0) + 1,
                500,
                dataTableEvent.search,
                copyType === 'copy-to' ? selectedVenueOption : selectedCopyVenueOption
            ),
        onSuccess: (response) => {
            const paginationData = response?.data?.data?.pagination as _Pagination;
            const responseData = response?.data?.data?.lists as _VenueUser;
            setVenueUsersListData(responseData);
            setDataTableEvent((event) => ({ ...event, totalRows: paginationData?.total }));
        },
        onError: (error) => logger.log('Failed to fetch admin staff list', error),
        refetchOnWindowFocus: false,
        enabled: false,
    });

    const venueListQueryRef = useRef(venueUsersListQuery);

    useEffect(() => {
        if (!selectedVenueOption) return;
        venueListQueryRef.current.refetch();
    }, [dataTableEvent?.forceUpdate]);

    useEffect(() => {
        const searchPayload = searchColumns.map((column) => `&search[${column}]=${debouncedSearchValue}`).join('');
        setDataTableEvent((event) => ({
            ...event,
            search: debouncedSearchValue.trim().length > 0 ? searchPayload : '',
            forceUpdate: event.forceUpdate + 1,
        }));
    }, [debouncedSearchValue]);

    const handleBulkCopy = () => {
        const usersPayload = buildUserCopyPayload(selectedProductsForBulkCopy);

        const copyUsersPayload: _UsersBulkCopyPayloadType = {
            tenantId: (copyType === 'copy-from' ? selectedCopyVenueOption : selectedVenueOption) ?? '',
            newTenantId: (copyType === 'copy-from' ? selectedVenueOption : selectedCopyVenueOption) ?? '',
            tenantStaffIds: usersPayload,
        };

        setIsCopyInProgress(true);

        bulkCopyUsers(
            userBulkCopyMutation,
            copyUsersPayload,
            (data: any) => {
                setToastMessage({
                    message: data?.message ?? '',
                    variant: AlertVariant.SUCCESS,
                    show: true,
                });
                onCopyFromSuccess();
                setIsCopyInProgress(false);
            },
            (error: string, variant: string) => {
                setToastMessage({
                    message: error,
                    variant,
                    show: true,
                });
                setIsCopyInProgress(false);
            },
            selectedVenueOption
        );
    };

    const footerContent = (
        <div className="mt-2">
            <MlxButton label="Cancel" icon="pi pi-times" size="small" onClick={() => setVisible(false)} className="p-button-text" />
            <MlxButton
                label="Proceed"
                icon="pi pi-check"
                size="small"
                onClick={() => {
                    handleBulkCopy();
                }}
                autoFocus
                disabled={selectedProductsForBulkCopy.length === 0 || isCopyInProgress}
            />
        </div>
    );

    const headerContent = (
        <div className="page-section">
            <div className="page-content">
                <div className="content-header flex-grow-0 p-0 pe-3">
                    <div>
                        {searchBarVisible && (
                            <>
                                {copyType === 'copy-from' && (
                                    <div className="d-flex">
                                        <small>{translatedText.COPY_MENU_ITEMS_FROM}&nbsp;</small>
                                        <span className="header-title mb-0">{` ${venueOptions?.find((option) => option.id === selectedCopyVenueOption)?.venueName}`}</span>{' '}
                                        <small>&nbsp;to&nbsp;</small>
                                        <span className="header-title mb-0">{` ${venueOptions?.find((option) => option.id === selectedVenueOption)?.venueName}`}</span>
                                    </div>
                                )}
                                {copyType === 'copy-to' && (
                                    <div className="d-flex">
                                        <small>{translatedText.COPY_MENU_ITEMS_FROM}&nbsp;</small>
                                        <span className="header-title mb-0">{` ${venueOptions?.find((option) => option.id === selectedVenueOption)?.venueName}`}</span>{' '}
                                        <small>&nbsp;to&nbsp;</small>
                                        <span className="header-title mb-0">{` ${venueOptions?.find((option) => option.id === selectedCopyVenueOption)?.venueName}`}</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div className="header-fields">
                        <MlxSearch
                            className="ms-2 p-inputtext-sm"
                            removeLabelOnMobile={true}
                            placeholder="Search"
                            value={searchValue}
                            onChange={(event) => setSearchValue(event.target.value)}
                            searchBarVisible={setSearchBarVisible}
                            isLoading={venueUsersListQuery?.isFetching && !!debouncedSearchValue.trim().length}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <MlxDialog
            visible={visible}
            onHide={() => setVisible(false)}
            header={headerContent}
            footer={footerContent}
            style={{ width: '90%', height: '90%' }}
        >
            <MlxBlockUI opacity={0.5}>
                <div className="page-section h-100">
                    <div className="page-content h-100">
                        <Divider className="content-divider" />
                        <div className="content-body flex-grow-1">
                            <VenueUsersList
                                openEditVenueSidebar={() => {}}
                                venueUsersListData={venueUsersListData}
                                setVenueUsersListData={setVenueUsersListData}
                                venueUsersListQuery={venueUsersListQuery}
                                selectedVenueOption={selectedVenueOption}
                                parent="copyUsers"
                                selectedProductsForBulkCopy={selectedProductsForBulkCopy}
                                setSelectedProductsForBulkCopy={setSelectedProductsForBulkCopy}
                                selectedCopyVenueOption={selectedCopyVenueOption}
                                copyType={copyType}
                            />
                        </div>
                    </div>
                </div>
            </MlxBlockUI>
        </MlxDialog>
    );
}

export default memo(CopyVenueUsersModal);
